import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { UserRole } from "@/types/team";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronDown, KeyRound, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";

interface UserProfileMenuViewProps {
  email: string;
  role: UserRole | null;
  onLogout: () => void;
}

export function UserProfileMenuView({
  email,
  role,
  onLogout,
}: UserProfileMenuViewProps) {
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleLogout = () => {
    onLogout();
    setLogoutDialogOpen(false);
  };

  const initial = email.charAt(0).toUpperCase();
  const localPart = email.split("@")[0];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-[9px] rounded-[10px] p-[9px] text-left",
              "border border-k-line bg-k-card",
              "transition-colors duration-160 ease-k-out",
              "hover:border-k-line-2",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-k-accent",
            )}
          >
            <span
              aria-hidden="true"
              className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-k-accent-soft font-k-display text-[12px] font-bold text-k-accent-ink"
            >
              {initial}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-k-display text-[12px] font-semibold text-k-ink-hi">
                {localPart}
              </span>
              {role && (
                <span className="truncate font-k-display text-[10px] capitalize text-k-ink-3">
                  {role}
                </span>
              )}
            </span>
            <ChevronDown className="h-[13px] w-[13px] shrink-0 text-k-ink-3" />
            <span className="sr-only">Account menu for {email}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-56">
          <DropdownMenuLabel>Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/change-password">
              <KeyRound className="mr-2 h-4 w-4" />
              <span>Change Password</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setLogoutDialogOpen(true)}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Logout</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to logout? You will need to sign in again to
              access your account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLogoutDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleLogout}>
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function UserProfileMenu() {
  const { session, role, signOut } = useAuth();

  if (!session?.user?.email) {
    return null;
  }

  return (
    <UserProfileMenuView
      email={session.user.email}
      role={role}
      onLogout={signOut}
    />
  );
}
